import { useEffect, useState } from 'react'

// useState that mirrors its value to localStorage, so navigation + filter choices
// survive a page refresh. Supports Set values (serialized as {__set:[...]}).
// Keys are namespaced by the caller (e.g. 'stat:tab').

function serialize(v) {
  if (v instanceof Set) return JSON.stringify({ __set: [...v] })
  return JSON.stringify(v)
}
function deserialize(s) {
  const o = JSON.parse(s)
  if (o && typeof o === 'object' && Array.isArray(o.__set)) return new Set(o.__set)
  return o
}

export function usePersistedState(key, initial) {
  const [v, setV] = useState(() => {
    try {
      const s = localStorage.getItem(key)
      if (s != null) return deserialize(s)
    } catch { /* private mode / bad JSON → fall back to initial */ }
    return typeof initial === 'function' ? initial() : initial
  })
  useEffect(() => {
    try { localStorage.setItem(key, serialize(v)) } catch { /* ignore quota/private-mode */ }
  }, [key, v])
  return [v, setV]
}

// In-memory store — lives as long as the JS runtime does. Survives client-side
// navigation (SPA route changes keep the module loaded) but is wiped by a full
// browser refresh, which re-executes the bundle from scratch.
const memStore = new Map()

// Like useState, but the value is remembered under `key` across unmount/remount
// (e.g. leaving a page and coming back) — and RESET to `initial` on a hard reload.
// Use for filters you want to keep while navigating, yet default fresh on refresh.
export function useEphemeralState(key, initial) {
  const [v, setV] = useState(() => (
    memStore.has(key) ? memStore.get(key) : (typeof initial === 'function' ? initial() : initial)
  ))
  useEffect(() => { memStore.set(key, v) }, [key, v])
  return [v, setV]
}
